import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import {
  DateType,
  Team as TeamT,
  User as UserT,
  Role as RoleT,
  Company,
} from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import { Task as TaskT } from 'api/tasks';
import type { GroupedFilter } from 'api/tasks/filter-types';
import { QueryCombinerType } from 'api/tasks/filter-types';
import type { EffortPlanningConfig } from 'components/organisms/effort-planning-modal/types';
import type { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';

export type Minutes = number;

export interface AvailabilityFilter {
  startDate: Moment;
  endDate: Moment;
  availableMinutes: number;
  roles: number[];
  users: number[];
}

export interface Range {
  fromDate: Moment;
  toDate: Moment;
}

export type SummaryApiUserId = string;
export type SummaryApiCompanyId = string;
export type SummaryApiRoleId = string;
export type PeopleViewChildrenSummaryId = string;
export type ProjectViewChildrenSummaryId = string;
export type BudgetUserBreakdownKey = string;

export enum ResourceManagementGroupBy {
  Users = 'PEOPLE',
  Projects = 'PROJECT',
}
export enum ResourceType {
  User = 'user',
  Team = 'team',
  Role = 'role',
  FinancialBudget = 'financial_budget',
}

export enum AllocationMethod {
  TotalHours = 'TOTAL',
  HoursPerDay = 'HOURS_PER_DAY',
  PercentageOfCapacity = 'PERCENTAGE_OF_CAPACITY',
}

export enum AllocationMedium {
  Soft = 'SOFT',
  Hard = 'HARD',
}

export enum AllocationMediumApi {
  Soft = 'soft',
  Hard = 'hard',
}

export interface AllocatedMinutes {
  [AllocationMedium.Hard]: Minutes;
  [AllocationMedium.Soft]: Minutes;
}

export interface UserIdentifier {
  type: ResourceType.User;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  otherUserIds?: number[];
  projectFinancialsBudgetId?: number;
}

export interface TeamIdentifier {
  type: ResourceType.Team;
  teamId: number;
  projectFinancialsBudgetId?: number;
}

export interface RoleIdentifier {
  type: ResourceType.Role;
  roleId: number;
  teamId: number;
  projectFinancialsBudgetId?: number;
}

export type ResourceIdentifier =
  | UserIdentifier
  | TeamIdentifier
  | RoleIdentifier;

export type SummaryResourceIdentifer = UserIdentifier | RoleIdentifier;

export interface AllocationFieldValue {
  fieldId: number;
  fieldValue: unknown;
}

export type AllocationWorkType = 'TASK' | 'PHASE' | 'MILESTONE';

interface AllocationBase {
  id: number;
  startDate: DateType;
  endDate: DateType;
  minutes: number;
  totalMinutes: number;
  previewText?: string;
  projectPhaseId?: number;
  billable: boolean;
  projectId: number;
  medium: AllocationMedium;
  sources?: AllocationSourceTask[];
  projectFinancialsBudgetId?: number;
  resourceRequestReferenceId?: number;
  includeSaturday?: boolean;
  includeSunday?: boolean;
  fields?: AllocationFieldValue[];
  workId?: number;
  workType?: AllocationWorkType;
}
interface AllocationWithUserId extends AllocationBase {
  type: ResourceType.User;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  overAllocations: OverAllocationApi[];
}
interface AllocationWithTeamId extends AllocationBase {
  type: ResourceType.Team;
  teamId: number;
  roleId: number;
}

export type Allocation = AllocationWithUserId | AllocationWithTeamId;

export type AllocationCreatePayload =
  | Omit<AllocationWithUserId, 'id'>
  | Omit<AllocationWithTeamId, 'id'>;

export type AllocationUpdatePayload = Allocation;

export type AllocationDeletePayload = Allocation;

export type AllocationBulkPayload = {
  create?: AllocationCreatePayload[];
  update?: AllocationUpdatePayload[];
  delete?: AllocationDeletePayload[];
};

export interface AllocationSplitPayload {
  allocationId: number;
  splitDate: string;
}

interface AllocationResponseBase extends AllocationInDetailBase {
  project: Project;
}
interface AllocationResponseWithUser extends AllocationResponseBase {
  user: UserT;
}
interface AllocationResponseWithTeam extends AllocationResponseBase {
  team: TeamT;
}

type AllocationResponse =
  | AllocationResponseWithUser
  | AllocationResponseWithTeam;

export interface AllocationBulkResponse {
  allocations: {
    allocation: AllocationResponse;
    status: boolean;
  }[];
}
export type AggregationMediumMinutes = {
  type: AllocationMedium;
  totalMinutes: number;
};

export type AggregationBudgetBreakdown = {
  maxEndDate: string;
  maxStartDate: string;
  rmLevelSummary: {
    hard: number;
    soft: number;
  };
  totalMinutes: number;
};

export interface AggregationBase {
  projectId: number;
  totalMinutes?: number; // TODO: Made this optional as we receive from BE, but not use in FE anymore
  mediumMinutes?: {
    types: AggregationMediumMinutes[];
  };
  allocatedMintutes: AllocatedMinutes;
  minStartDate?: string | null;
  maxEndDate?: string | null;
  budgetBreakdown: Record<string, AggregationBudgetBreakdown>;
}
export interface UserLevelAggregation extends AggregationBase {
  userId: number;
  type: ResourceType.User;
  roleId?: number;
  referenceTeamId?: number;
}
export interface TeamLevelAggregation extends AggregationBase {
  teamId: number;
  type: ResourceType.Team;
}
export interface AllocationInDetailBase {
  id: number;
  startDate: DateType;
  endDate: DateType;
  minutes: number;
  previewText?: string;
  projectPhase?: {
    id: number;
    name: string;
  };
  billable: boolean;
  medium: AllocationMedium;
  includeSaturday?: boolean;
  includeSunday?: boolean;
  fields?: AllocationFieldValue[];
  workId?: number;
  workType?: AllocationWorkType;
}

export interface AllocationForTeam extends AllocationInDetailBase {
  type: ResourceType.Team;
  team: Pick<TeamT, 'teamId'>;
  project: Project;
  projectFinancialsBudgetId?: number;
}

export interface AllocationForProjectWithUser extends AllocationInDetailBase {
  type: ResourceType.User;
  user: UserT;
  role?: RoleT;
  referenceTeam?: TeamT;
}
export interface AllocationForProjectWithTeam extends AllocationInDetailBase {
  type: ResourceType.Team;
  team: TeamT;
}

export type AllocationForProject =
  | AllocationForProjectWithUser
  | AllocationForProjectWithTeam;

export interface AllocationsGroupByProject {
  project: Project;
  allocations: AllocationForProject[];
  aggregations: (UserLevelAggregation | TeamLevelAggregation)[];
}

export interface UserIdWithAvailableMinutes {
  user: Pick<UserT, 'userId'>;
  fulfilledMinutes: number;
  utilization?: number;
  margin?: number;
  cost?: number;
}

export interface AvailabilityForTeamWithProject {
  project: Pick<Project, 'projectId'>;
  team: Pick<TeamT, 'teamId'>;
  requiredMinutes: number;
  matches: UserIdWithAvailableMinutes[];
}

export interface UserSocketIdentifier extends Pick<UserT, 'userId'> {
  identityType: 'USER';
}

export interface TeamSocketIdentifier extends Pick<TeamT, 'teamId'> {
  identityType: 'TEAM';
}

export interface CompanySocketIdentifier extends Pick<Company, 'companyId'> {
  identityType: 'COMPANY';
}

type TeamWithId = TeamT & TeamSocketIdentifier;

export interface TeamWithAdditionalInfo
  extends Omit<TeamWithId, 'userList' | 'companyList'> {
  accountId: number;
  userList?: UserSocketIdentifier[];
  companyList?: CompanySocketIdentifier[];
}

export interface TeamReplacementSocketData extends TeamSocketIdentifier {
  accountId: number;
  userList?: UserSocketIdentifier[];
  companyList?: CompanySocketIdentifier[];
}

export type SocketDataForTeam = TeamSocketIdentifier | TeamWithAdditionalInfo;

export interface SocketDataForTeamInRm {
  team: SocketDataForTeam;
}

/** Resource Management Config */
export interface SoftAllocationConfig {
  enabled: boolean;
  meta: {
    templateDefault: boolean;
  };
}

export interface CollaboratorAccessConfig {
  hasViewAccess: boolean;
  hasEditAccess: boolean;
}

export interface AccessConfig {
  collaboratorAccessMeta: CollaboratorAccessConfig;
}

export interface TrackAllocationsAgainstConfig {
  isPhasesAllowed: boolean;
  isBudgetsAllowed: boolean;
  isMilestonesAllowed: boolean;
  isTasksAllowed: boolean;
}

export enum PlanningLevelConfig {
  HoursPerDay = 'HOURS_PER_DAY',
  HoursPerWeek = 'HOURS_PER_WEEK',
}

export interface ResourceManagementConfig {
  softAllocations: SoftAllocationConfig;
  accessConfig: AccessConfig;
  planningLevelConfig: PlanningLevelConfig;
  resourceApproverIds: number[];
  effortPlanning: EffortPlanningConfig;
  trackAllocationsAgainst?: TrackAllocationsAgainstConfig;
  allocationPreviewTemplate?: string;
}

export enum ResourceManagementApiZoomType {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
}
export interface ResourceManagementExportParams {
  startDate: Moment;
  endDate: Moment;
  groupBy: ResourceManagementGroupBy;
  zoomType: ResourceManagementApiZoomType;
  filter: GroupedFilter;
}

export interface ResourceManagementFilterParams {
  groupBy: ResourceManagementGroupBy;
  filter: GroupedFilter;
}

export interface FilteredProjectInPeople {
  projectId: number;
  referenceTeamId?: number;
  roleId?: number;
}

export interface FilteredUserInPeople {
  type: ResourceType.User;
  userId: number;
  projects: FilteredProjectInPeople[];
}

export interface FilteredTeamInPeople {
  projectId: number;
  teamId: number;
}

export interface FilteredRoleInPeople {
  type: ResourceType.Role;
  roleId: number;
  projects: FilteredTeamInPeople[];
}

export interface FilteredResponseInPeople {
  peopleTabEntities: (FilteredUserInPeople | FilteredRoleInPeople)[];
}

export interface FilteredResourceInProjects {
  projectId: number;
}

export interface FilteredResponseInProjects {
  projectTabEntities: FilteredResourceInProjects[];
}

export type ResourceManagementFilterResponse =
  | FilteredResponseInPeople
  | FilteredResponseInProjects;

export interface DeleteAllocationsProps {
  projectId: number;
  resourceIdentifier: SummaryResourceIdentifer;
}

export interface ConvertAllocationsProps {
  projectId: number;
  resourceIdentifier?: SummaryResourceIdentifer; // resourceIdentifier is not needed for project-level conversion
  events?: AllocationEventModel[];
}

export interface ConvertAllocationsPropsApi extends ConvertAllocationsProps {
  medium: AllocationMedium;
}

/** V2 Types */
export interface ResourceAllocationApiDateRange {
  startDate: DateType;
  endDate: DateType;
}
export interface GetResourceAllocationPayload
  extends ResourceAllocationApiDateRange {
  groupBy?: ResourceManagementGroupBy;
  includeSummaries?: boolean;
  ignoreSoftAllocation?: boolean;
  includeOverAllocations?: boolean;
  includeEmptySummaries?: boolean;
  userIdList?: number[];
  roleIdList?: number[];
  projectIdList?: number[];
  filter?: GroupedFilter;
}

export interface GetResourceAllocationDemandPayload
  extends ResourceAllocationApiDateRange {
  roleIdList: number[];
  zoomLevel: ResourceManagementApiZoomType;
  ignoreSoftAllocation: boolean;
  filter: GroupedFilter;
  isHoursView: boolean;
}

export enum FetchableResourceType {
  UserList = 'userIdList',
  RoleList = 'roleIdList',
  ProjectList = 'projectIdList',
}

export type ListOfResourcesToBeFetched = [number] | number[];

export type TypesOfResourcesToBeFetched =
  | Dictionary<boolean>
  | ListOfResourcesToBeFetched;

export type ResourcesToBeFetched<T extends TypesOfResourcesToBeFetched> = {
  [Property in FetchableResourceType]: T;
} & {
  pageSource?: ResourceManagementGroupBy;
};

export type ResourcesLoadedInCurrentView = ResourcesToBeFetched<
  Dictionary<boolean>
>;

export interface LoadAllocationsData<T extends ListOfResourcesToBeFetched> {
  resources: ResourcesToBeFetched<T>;
  range: Range;
}

export interface AllocatedMinutesApi {
  [AllocationMediumApi.Hard]: Minutes;
  [AllocationMediumApi.Soft]: Minutes;
}

export interface OverAllocationApi {
  startDate: DateType;
  endDate: DateType;
  overAllocatedMinutes: Minutes;
}

interface BaseResourceSummary {
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutesApi;
}

export type TeamResourceSummary = BaseResourceSummary;

export type CompanyResourceSummary = BaseResourceSummary;

export interface GenericResourceSummary extends BaseResourceSummary {
  capacityMinutes?: Minutes;
  includeSaturday?: boolean;
  includeSunday?: boolean;
}

export interface UserResourceSummary extends BaseResourceSummary {
  capacityMinutes: Minutes;
}

export type BudgetUserBreakdownValue = Record<string, GenericResourceSummary[]>;

export type ResourceSummariesData = {
  userSummaryMap: Record<SummaryApiUserId, UserResourceSummary[]>;
  roleSummaryMap: Record<SummaryApiRoleId, TeamResourceSummary[]>;
  companySummaryMap: Record<SummaryApiCompanyId, CompanyResourceSummary[]>;
  childrenBreakdown: Record<
    PeopleViewChildrenSummaryId | ProjectViewChildrenSummaryId,
    GenericResourceSummary[]
  >;
  budgetUserBreakdown: Record<BudgetUserBreakdownKey, BudgetUserBreakdownValue>;
};

export interface AllocationForUser extends AllocationInDetailBase {
  type: ResourceType.User;
  user: Pick<UserT, 'userId'>;
  project: Project;
  projectFinancialsBudgetId?: number;
  role?: RoleT;
  referenceTeam?: TeamT;
  overAllocations: OverAllocationApi[];
}

export interface AllocationsGroupByUser {
  type: ResourceType.User;
  user: UserT;
  allocations: AllocationForUser[];
  summaries: UserResourceSummary[];
  aggregations: UserLevelAggregation[];
  companySummaries: CompanyResourceSummary[];
}

export interface AllocationsGroupByUserLookup
  extends Omit<AllocationsGroupByUser, 'aggregations'> {
  aggregationLookup: Dictionary<UserLevelAggregation>;
}

export interface AllocationsGroupByTeam {
  type: ResourceType.Team;
  team: TeamT;
  allocations: AllocationForTeam[];
  summaries: TeamResourceSummary[];
  aggregations: TeamLevelAggregation[];
  companySummaries: CompanyResourceSummary[];
}
export interface AllocationsGroupByTeamLookup
  extends Omit<AllocationsGroupByTeam, 'aggregations'> {
  aggregationLookup: Dictionary<TeamLevelAggregation>;
}

export interface AllocationsGroupByUsersOrTeams {
  resources: (AllocationsGroupByUser | AllocationsGroupByTeam)[];
  groupBy: ResourceManagementGroupBy.Users;
  startDate: DateType;
  endDate: DateType;
  zoomLevel: ResourceManagementApiZoomType;
  summaries: ResourceSummariesData;
  count?: number;
}
export type AllocationsGroupByUsersOrTeamsLookup = Dictionary<
  AllocationsGroupByUserLookup | AllocationsGroupByTeamLookup
>;

export type ResourceAllocations = (
  | AllocationsGroupByUser
  | AllocationsGroupByTeam
)[];

export type ResourceAllocationsAndSummaries = {
  allocations: ResourceAllocations;
  summaries: ResourceSummariesData;
};

export type AvailabilityFilterApiWithPlanningLevel = AvailabilityFilter & {
  isHoursPerWeek: boolean;
  ignoreSoftAllocation?: boolean;
};

export interface DemandSummary {
  userSummary: AllocatedMinutesApi;
  teamSummary: AllocatedMinutesApi;
}

export interface CapacitySummary {
  teamMembersCapacityFte: number;
  partnerCapacityFte: number;
  timeOffFte: number;
  holidaysFte: number;
  teamMembersCapacityMinutes: number;
  partnerCapacityMinutes: number;
  timeOffMinutes: number;
  holidaysMinutes: number;
}

export interface DemandTabProjects extends Pick<Project, 'projectId'> {
  allocationFte: number;
  softAllocationFte?: number;
  hardAllocationFte?: number;
  allocationMinutes: number;
}
export interface DemandResponseUnit extends ResourceAllocationApiDateRange {
  capacityMinutes: Minutes;
  summary: DemandSummary;
  capacityDetails: CapacitySummary;
  totalAllocationFte: number;
  teamAllocationFte: number;
  userAllocationFte: number;
  totalAllocationMinutes: number;
  teamAllocationMinutes: number;
  userAllocationMinutes: number;
  projectAllocationSummaryList: DemandTabProjects[];
}

export interface RoleWiseDemandResponse {
  role: RoleT;
  demandResponseUnits: DemandResponseUnit[];
  projectIdToAllocationFteMap: Record<number, number>;
  projectIdToAllocationMinutesMap: Record<number, number>;
  totalAllocationForRoleFte: number;
  totalCapacityForRoleFte: number;
  totalAllocationForRoleMinutes: number;
  totalCapacityForRoleMinutes: number;
}

export interface DemandGraphUnits {
  startDate: string;
  endDate: string;
  holidayFte: number;
  timeOffFte: number;
  availableCapacityFte: number;
  userAllocationFte: number;
  teamAllocationFte: number;
  actualCapacityFte: number;
  holidayMinutes: number;
  timeOffMinutes: number;
  availableCapacityMinutes: number;
  userAllocationMinutes: number;
  teamAllocationMinutes: number;
  actualCapacityMinutes: number;
}

export interface ResourceAllocationDemandResponse
  extends Omit<GetResourceAllocationDemandPayload, 'roleIdList' | 'filter'> {
  roleWiseDemandResponses: RoleWiseDemandResponse[];
}

export interface ResourceAllocationDemandGraphResponse
  extends Omit<GetResourceAllocationDemandPayload, 'roleIdList' | 'filter'> {
  demandTabGraphResponseUnits: DemandGraphUnits[];
}

export enum AllocationSourceType {
  Task = 'TASK',
  Phase = 'PROJECT_PHASE',
}

interface AllocationSourceBase {
  sourceId: number;
}

export interface AllocationSourceTask extends AllocationSourceBase {
  sourceType: AllocationSourceType.Task;
}

export interface AllocationSourcePhase extends AllocationSourceBase {
  sourceType: AllocationSourceType.Phase;
}

export type AllocationSource =
  | ({
      metaData: ProjectPhase;
    } & AllocationSourcePhase)
  | ({
      metaData: TaskT;
    } & AllocationSourceTask);

export interface AllocatedDailyMinutes {
  dailyAllocatedMinutes: Map<string, number>;
  projectId: number;
}

interface RmPermission {
  resourceAllocationRead: boolean;
  resourceAllocationManage: boolean;
}

interface MemberAllocationPermission {
  [key: number]: RmPermission;
}

interface ProjectRmPermission {
  projectAllocationPermission: RmPermission;
  memberAllocationPermission: MemberAllocationPermission;
}

export interface ResourceManagementPermissions {
  [key: number]: ProjectRmPermission;
}

export interface ResourceManagementPermissionsResponse {
  [key: number]: {
    canManage: boolean;
  };
}

export enum RMPermissionsApiParamType {
  ResourceAI = 'RESOURCE_AI',
  ResourceManagement = 'RESOURCE_MANAGEMENT',
  TimeSheets = 'TIME_SHEETS',
}

export interface ResourceManagementFilterPayload {
  offset?: number;
  page?: number;
  limit?: number;
  getRangeProjects?: boolean;
  visibleStartDate?: DateType;
  visibleEndDate?: DateType;
  projectId?: number;
  match?: QueryCombinerType;
  searchValue?: string;
  additionalFields?: Record<string, string>;
}

export type PaginatedPeoplePayload = {
  type: 'USERS_PAGINATED';
  filter: ResourceManagementFilterPayload;
};
export interface PaginatedPeopleResponse {
  userResponse: {
    users: UserT[];
    totalCount: number;
    currentPage: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    totalPages: number;
  };
}

export type PaginatedProjectsPayload = ResourceManagementFilterPayload;
export interface PaginatedProjectsResponse {
  data: Project[];
  totalCount: number;
  page: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  totalPages: number;
}

export interface PaginatedProjectsForUserPayload {
  type: 'PEOPLE_TO_PROJECT';
  userPaginationRequests?: {
    id: number;
    page?: number;
    userTeamMembershipId?: number;
  }[];
  teamPaginationRequests?: {
    id: number;
    companyId?: number;
    page?: number;
    projectTeamId?: number;
  }[];
  filter: ResourceManagementFilterPayload;
}
export interface PaginatedProjectsForUserResponseDataItem {
  projectId: number;
  project: Project;
  role?: RoleT;
  refTeam?: TeamT;
  userTeamMembershipId?: number;
  memberBudgetIds?: number[];
}
export interface PaginatedProjectsForUserResponseData {
  data: PaginatedProjectsForUserResponseDataItem[];
  page: number;
  count: number;
  totalPages: number;
  hasMore: boolean;
}
export interface PaginatedProjectsForTeamResponseDataItem {
  projectId: number;
  project: Project;
  refTeam: TeamT;
  projectTeamId?: number;
  memberBudgetIds?: number[];
}
export interface PaginatedProjectsForTeamResponseData {
  data: PaginatedProjectsForTeamResponseDataItem[];
  page: number;
  count: number;
  totalPages: number;
  hasMore: boolean;
}

export interface PaginatedProjectsForUserResponse {
  userProjects: Record<number, PaginatedProjectsForUserResponseData>;
  teamProjects: Record<string, PaginatedProjectsForTeamResponseData>;
}

export interface GetPlaceholdersPayload {
  type: 'PLACEHOLDER_COUNT';
  filter: ResourceManagementFilterPayload;
}
export interface GetPartnersPayload {
  type: 'PARTNER_USERS_TIMELINE';
  filter: ResourceManagementFilterPayload;
}

export interface RmPlaceholderMeta {
  roleId: number;
  roleName: string;
  projectCount: number;
}

export interface PartnerCompanyAsRmMeta {
  users: UserT[];
  company: Company;
}
export interface GetPlaceholdersResponse {
  roleToProjectCounts: RmPlaceholderMeta[];
  partners: PartnerCompanyAsRmMeta[];
}
export interface GetPartnersResponse {
  partnerResponse: PartnerCompanyAsRmMeta[];
}

export interface PaginatedResourcesForProjectPayload {
  projects?: {
    projectId: number;
    budgetId?: number;
    offset: number;
  }[];
  filter: ResourceManagementFilterPayload;
}

export type PaginatedResourcesForProjectResponseDataUserItem = {
  user: UserT;
  role?: RoleT;
  referenceTeam?: TeamT;
};

export type PaginatedResourcesForProjectResponseDataTeamItem = {
  role?: RoleT;
  team: TeamT;
};
export interface PaginatedResourcesForProjectResponseData {
  members: Array<
    | PaginatedResourcesForProjectResponseDataUserItem
    | PaginatedResourcesForProjectResponseDataTeamItem
  >;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const rmFilterToApiFormat = (
  filter: ResourceManagementFilterPayload,
) => {
  const { additionalFields, ...rest } = filter;

  return {
    ...rest,
    ...additionalFields,
  };
};
